import React from "react";
import { motion } from "framer-motion";
import { MessageSquare, Smartphone, Zap } from "lucide-react";

const WhatWeOffer = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 },
    },
  };

  const reasonsData = [
    {
      id: 1,
      title: "Multi-Purpose Messaging",
      description:
        "Send all kinds of messages to as many people as possible, quickly and effortlessly.",
      icon: <MessageSquare className="w-8 h-8 text-white" />,
      bgColor: "from-red-400 to-red-600",
      shadowColor: "shadow-red-200",
    },
    {
      id: 2,
      title: "Omni-Channel Communication",
      description:
        "Pick between Quick SMS or Bulk SMS for the best method to broadcast your message.",
      icon: <Smartphone className="w-8 h-8 text-white" />,
      bgColor: "from-red-400 to-red-600",
      shadowColor: "shadow-red-200",
    },
    {
      id: 3,
      title: "Quick & Easy Setup",
      description:
        "Sign up for a free account and send your first message blast within seconds!",
      icon: <Zap className="w-8 h-8 text-white" />,
      bgColor: "from-red-400 to-red-600",
      shadowColor: "shadow-emerald-200",
    },
  ];

  return (
    <section className="overflow-hidden py-24 bg-gradient-to-b from-white to-red-50">
      <div className="relative">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-red-50 rounded-full opacity-70 mix-blend-multiply filter blur-xl"></div>
        <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-96 h-96 bg-red-50 rounded-full opacity-70 mix-blend-multiply filter blur-xl"></div>
        <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-emerald-50 rounded-full opacity-50 mix-blend-multiply filter blur-xl"></div>
      </div>

      <div className="relative z-10 px-4 mx-auto max-w-8xl sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
          className="mb-20 text-center"
        >
          <motion.div
            variants={itemVariants}
            className="inline-block px-4 py-1 mb-3 bg-red-100 rounded-full"
          >
            <span className="font-medium text-red-700">Why Choose Us</span>
          </motion.div>
          <motion.h2
            variants={itemVariants}
            className="mb-6 text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-600 md:text-6xl"
          >
            Reasons To Choose TXTConnect.
          </motion.h2>
          <motion.p
            variants={itemVariants}
            className="mx-auto max-w-3xl text-xl text-gray-600"
          >
            TXTConnect is the most effective online mass communication platform
            for organizations in any industry.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={containerVariants}
          className="grid grid-cols-1 gap-10 md:grid-cols-3"
        >
          {reasonsData.map((reason, index) => (
            <motion.div
              key={reason.id}
              variants={itemVariants}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              className="flex flex-col items-center p-8 text-center bg-white rounded-2xl shadow-lg transition-all duration-300 hover:shadow-xl"
            >
              <div className="flex mb-6">
                <div className="relative">
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${reason.bgColor} rounded-full transform rotate-6 opacity-20`}
                  ></div>
                  <div
                    className={`relative bg-gradient-to-br ${reason.bgColor} w-20 h-20 rounded-full flex items-center justify-center shadow-lg ${reason.shadowColor}`}
                  >
                    <span className="text-3xl font-bold text-white">
                      {reason.id}
                    </span>
                  </div>
                </div>
              </div>
              <h3 className="mb-3 text-2xl font-bold text-gray-800">
                {reason.title}
              </h3>
              <p className="text-gray-600">{reason.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default WhatWeOffer;
